import React, { useContext, useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation, Navigate } from 'react-router-dom';
import TitleContext from './TitleContext';
import { accessToPath, refreshPermissions } from '../comman/permissions';

const HelmetWrapper = ({ children, title, route }) => {
  const { setTitle } = useContext(TitleContext);
  const location = useLocation();
  // console.log(route);

  // A title may be a function of the location. /menu_creation/:mode is one route serving two
  // screens -- edit and duplicate -- which need different titles, and a static string can only
  // ever be right for one of them.
  const resolvedTitle = typeof title === 'function' ? title(location) : title;

  useEffect(() => {
    setTitle(resolvedTitle);
    document.title = resolvedTitle;
  }, [resolvedTitle, location.pathname, setTitle]);

  // Permissions are re-read on every navigation rather than cached in localStorage. A stored
  // answer goes stale the moment somebody enables a screen or edits a role, and the user then has
  // to log out and back in to be told something that is already true.
  //
  // Not on /login: there is nobody to read permissions for yet, and the login screen must open
  // whatever the masters say.
  const isLoginRoute = route?.path === '/login';

  const userRole = parseInt(localStorage.getItem('userRole'), 10); // Parse as integer
  // Signed in at all? A role that does not parse is nobody -- not logged in, or a session from
  // before roles were stored -- and that is the one case where the login page is the right answer.
  const isSignedIn = Number.isInteger(userRole);

  // Nothing to look up for a visitor with no account and nothing to look it up by: the login page
  // must open whatever the masters say, and somebody who is not signed in is being sent there
  // rather than shown this route at all.
  const skipCheck = isLoginRoute || !isSignedIn;
  const [checked, setChecked] = useState(skipCheck);

  useEffect(() => {
    if (skipCheck) {
      setChecked(true);
      return undefined;
    }
    let cancelled = false;
    setChecked(false);
    refreshPermissions().finally(() => {
      if (!cancelled) setChecked(true);
    });
    return () => { cancelled = true; };
  }, [location.pathname, skipCheck]);

  // Superadmin (role=0) has unrestricted access to every route in the portal.
  const isSuperadmin = userRole === 0;
  // A route may be the SuperAdmin's alone -- Screen Access is. That is the one thing a route
  // still says about itself.
  //
  // There is no list of role numbers on a route any more. There were 133 of them, written when
  // the roles were 0 to 11, and a role added since -- Guest, Sevarthi, POC -- appeared in none:
  // a Guest with View on Menu Creation was bounced off /menu_creation/view because that route
  // listed roles 1 and 2. No field in the portal could add a role to those lists; they could only
  // be edited in a file, in a deploy.
  //
  // What decides it is screenAccess below: the departments a person works in say which screens
  // they have, and their role's operations say what they may do there.
  const canAccessRoute = route?.superadminOnly ? isSuperadmin : true;

  // The masters have the second say. Hiding a screen from the menu was never the same as taking
  // it away -- the URL still opened -- so Disable in Screen Master meant "not in the menu" rather
  // than "out of service", and a role without the operation could still type its way in.
  //
  // Not a substitute for the server: every endpoint checks the role itself, because anyone can
  // POST a URL. This is the door on the room, not the lock on the safe.
  const screenAccess = accessToPath(route?.path ?? location.pathname);

  // Not signed in: the login page, and nothing else. This is checked before the screen is drawn
  // and before the "no access" card, because neither is the right answer for somebody the portal
  // does not know yet -- and it covers every route, including the ones that are nobody's screen
  // (the dashboard, Profile, Change Password), which used to render the app's chrome to a visitor
  // who had never signed in.
  //
  // `from` is where they were headed, so signing in takes them there rather than to the dashboard.
  if (!isSignedIn && !isLoginRoute) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Nothing is drawn until the answer is in. Rendering the screen first and snatching it back, or
  // showing "no access" before the check has run, are both worse than a moment of blank.
  if (!checked) {
    return (
      <>
        <Helmet>
          <title>{resolvedTitle}</title>
        </Helmet>
        <div className="p-8 text-sm text-gray-500">Checking access...</div>
      </>
    );
  }

  if (isLoginRoute) {
    return (
      <>
        <Helmet>
          <title>{resolvedTitle}</title>
        </Helmet>
        {children}
      </>
    );
  }else if (canAccessRoute && !screenAccess.allowed) {
    // Signed in and allowed the route, but the screen itself is closed to them. Bouncing to the
    // login page would be a lie -- they are logged in, and logging in again changes nothing --
    // so this says which of the two reasons it is and leaves them where they are.
    return (
      <>
        <Helmet>
          <title>{resolvedTitle}</title>
        </Helmet>
        <div className="p-8">
          <div className="max-w-xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h1 className="text-lg font-bold text-gray-900">No access to this screen</h1>
            <p className="mt-2 text-sm text-gray-600">
              {screenAccess.reason === 'inactive'
                ? 'This screen has been made inactive in Screen Master, so it is out of service for everyone.'
                : `Your role does not have the "${screenAccess.operation}" operation on this screen.`}
            </p>
            <p className="mt-4 text-sm text-gray-500">
              An administrator can change this in Screen Master, Role Master or User Master.
            </p>
          </div>
        </div>
      </>
    );
  }else if (canAccessRoute) {
    // Render the route if user has access
    return (
      <>
        <Helmet>
          <title>{resolvedTitle}</title>
        </Helmet>
        {children}
      </>
    );
  }else {
    // Signed in -- anyone who is not was sent to /login above -- but this route is not theirs:
    // Screen Access, which is the SuperAdmin's alone. The dashboard, not the login page: they are
    // logged in, and sending them to log in again suggests the wrong remedy for the wrong problem.
    return <Navigate to="/" replace />;
  }
  
};

export default HelmetWrapper;
